import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { captureException } from '@sentry/react'

import { Appearance } from './Appearance'
import { AppReduxStore, createStore } from './app/store'
import { QodanaErrorBoundary } from './components/QodanaErrorBoundary/QodanaErrorBoundary'
import { loadConfig } from './config/runtime'
import { initializeDataApi } from './app/api'
import { usePageTitle } from './hooks/usePageTitle'
import { PreparingAppLoader } from './components/PreparingAppLoader/PreparingAppLoader'
import { UnknownError } from './components/Errors'

if (typeof process.env.NODE_ENV !== 'undefined' && process.env.NODE_ENV === 'development') {
	import('./development/console')
}

const ConfiguredApp = lazy(() => import('./layout/ConfiguredApp/ConfiguredApp'))

const DEFAULT_QUERY_ERROR_ALERT_TYPE = 'ui'
const DEFAULT_SUPPRESSED_QUERY_ERRORS = ['no_license_agreement', 'no_credentials_provided']

export default function App() {
	const [store, setStore] = useState<AppReduxStore>()
	const [error, setError] = useState<unknown>()

	usePageTitle()

	useEffect(() => {
		;(async () => {
			try {
				const config = await loadConfig()

				initializeDataApi(
					config.dataApiBaseUrl,
					config.queryErrorAlertType || DEFAULT_QUERY_ERROR_ALERT_TYPE,
					config.suppressedQueryErrors || DEFAULT_SUPPRESSED_QUERY_ERRORS,
				)
				setStore(createStore())
			} catch (err) {
				captureException(err)
				setError(err)
			}
		})()
	}, [])

	if (error) {
		return (
			<Appearance>
				<Router>
					<UnknownError />
				</Router>
			</Appearance>
		)
	}

	if (!store) {
		return (
			<Appearance>
				<Router>
					<PreparingAppLoader />
				</Router>
			</Appearance>
		)
	}

	return (
		<Provider store={store}>
			<Appearance>
				<Router>
					<Routes>
						<Route
							path="*"
							element={
								<QodanaErrorBoundary errorTemplate={<UnknownError />}>
									<Suspense fallback={<PreparingAppLoader />}>
										<ConfiguredApp />
									</Suspense>
								</QodanaErrorBoundary>
							}
						/>
					</Routes>
				</Router>
			</Appearance>
		</Provider>
	)
}
